import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { DesempenhoAtendente } from '@/types/dashboard';

interface TabelaDesempenhoAtendentesProps {
  dados: DesempenhoAtendente[];
}

type SortKey = 'nome' | 'atendidos' | 'finalizados' | 'tempoMedio' | 'emAndamento';
type SortOrder = 'asc' | 'desc';

function getDesempenhoTier(taxa: number): 'ink' | 'default' | 'soft' {
  if (taxa >= 80) return 'ink';
  if (taxa >= 60) return 'default';
  return 'soft';
}

function getDesempenhoKey(taxa: number): string {
  if (taxa >= 80) return 'excellent';
  if (taxa >= 60) return 'good';
  return 'regular';
}

export function TabelaDesempenhoAtendentes({ dados }: TabelaDesempenhoAtendentesProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('finalizados');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const dadosOrdenados = [...dados].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortOrder === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  const melhorAtendente =
    dados.length > 0
      ? dados.reduce((melhor, atual) => {
          const taxaMelhor =
            melhor.atendidos > 0 ? (melhor.finalizados / melhor.atendidos) * 100 : 0;
          const taxaAtual =
            atual.atendidos > 0 ? (atual.finalizados / atual.atendidos) * 100 : 0;
          return taxaAtual > taxaMelhor ? atual : melhor;
        }, dados[0])
      : null;

  if (dados.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        {t('dashboard.tables.noAgents')}
      </div>
    );
  }

  const sortArrow = (k: SortKey) => (sortKey === k ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <div className="flex flex-col gap-3">
      {dados.length > 1 && melhorAtendente && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border-soft">
          <Trophy className="h-5 w-5 text-foreground flex-shrink-0" />
          <div>
            <p className="font-medium text-sm">{t('dashboard.tables.periodHighlight')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              <span className="font-medium text-foreground">{melhorAtendente.nome}</span>{' '}
              {t('dashboard.tables.finishedCount', { count: melhorAtendente.finalizados })}
            </p>
          </div>
        </div>
      )}

      <div className="tbl-card">
        <table className="tbl">
          <thead>
            <tr>
              <th onClick={() => handleSort('nome')} style={{ cursor: 'pointer' }}>
                {t('dashboard.tables.agent')}{sortArrow('nome')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('atendidos')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.attended')}{sortArrow('atendidos')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('finalizados')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.finished')}{sortArrow('finalizados')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('tempoMedio')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.averageTime')}{sortArrow('tempoMedio')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('emAndamento')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.ongoing')}{sortArrow('emAndamento')}
              </th>
              <th>{t('dashboard.tables.performance')}</th>
            </tr>
          </thead>
          <tbody>
            {dadosOrdenados.map((atendente) => {
              const taxaFinalizacao =
                atendente.atendidos > 0
                  ? Math.round((atendente.finalizados / atendente.atendidos) * 100)
                  : 0;
              const tier = getDesempenhoTier(taxaFinalizacao);
              const label = t(`dashboard.tables.${getDesempenhoKey(taxaFinalizacao)}`);
              const isMelhor = atendente.id === melhorAtendente?.id && dados.length > 1;
              const pillVariant =
                tier === 'ink'
                  ? 'status-pill--ink'
                  : tier === 'soft'
                    ? 'status-pill--soft'
                    : '';

              return (
                <tr key={atendente.id} style={{ cursor: 'default' }}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="tbl-avt" aria-hidden>
                        {atendente.nome.charAt(0)}
                      </span>
                      <span className="font-medium">{atendente.nome}</span>
                      {isMelhor && (
                        <Trophy className="h-3.5 w-3.5 text-foreground flex-shrink-0" />
                      )}
                    </div>
                  </td>
                  <td className="num mono">{atendente.atendidos}</td>
                  <td className="num mono">
                    {atendente.finalizados}
                    <span className="text-muted-foreground"> / </span>
                    {atendente.perdidos}
                  </td>
                  <td className="num mono">{atendente.tempoMedio}h</td>
                  <td className="num mono">{atendente.emAndamento}</td>
                  <td>
                    <span className={`status-pill ${pillVariant}`}>
                      {label} ({taxaFinalizacao}%)
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="status-pill status-pill--ink" style={{ padding: '1px 7px', fontSize: 9 }}>
            {t('dashboard.tables.excellent')}
          </span>
          ≥ 80%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="status-pill" style={{ padding: '1px 7px', fontSize: 9 }}>
            {t('dashboard.tables.good')}
          </span>
          ≥ 60%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="status-pill status-pill--soft" style={{ padding: '1px 7px', fontSize: 9 }}>
            {t('dashboard.tables.regular')}
          </span>
          &lt; 60%
        </span>
      </div>
    </div>
  );
}
